/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { i18n } from 'config/locales';
import * as formTypes from 'constants/formTypes';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonRequired, CommonOnError
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Text from 'components/Form/Text';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import DateControl from 'components/Form/DateControl';
import PVQLocation from 'components/Form/PVQLocation';
import { CitizenshipIntlItemModel } from 'forms/sections/citizenship/multiple/types';
import DateRange from 'components/Form/DateRange';
import TravelDaysPVQ from 'components/Section/Foreign/Travel/TravelDaysPvq';
import TravelPurpose from 'components/Section/Foreign/Travel/TravelPurpose';

interface Props extends Partial<CitizenshipIntlItemModel>, ValidationElementProps {
  attribute: string
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: formTypes.FormTypes
}

export default class CitizenshipIntlItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
  }

  static defaultProps: Partial<Props> = {
    onUpdate: () => { },
    scrollIntoView: false,
    onError: () => {},
    required: false,
    formType: undefined,
  };

  update = (queue) => {
    this.props.onUpdate({
      Has: this.props.Has,
      Threatened: this.props.Threatened,
      ThreatenedExplain: this.props.ThreatenedExplain,
      InfoGrab: this.props.InfoGrab,
      InfoGrabExplain: this.props.InfoGrabExplain,
      Interest: this.props.Interest,
      InterestExplain: this.props.InterestExplain,
      SecurityIncidents: this.props.SecurityIncidents,
      SecurityIncidentsExplain: this.props.SecurityIncidentsExplain,
      ForeignContact: this.props.ForeignContact,
      ForeignContactExplain: this.props.ForeignContactExplain,
      PoliceEncountersExplain: this.props.PoliceEncountersExplain,
      PoliceEncounters: this.props.PoliceEncounters,
      SearchedExplain: this.props.SearchedExplain,
      Searched: this.props.Searched,
      WhyTravel: this.props.WhyTravel,
      WhenTraveled: this.props.WhenTraveled,
      CountryTraveledTo: this.props.CountryTraveledTo,
      Days: this.props.Days,
      Purpose: this.props.Purpose,
      ...queue,
    });
  };

  updatePurpose = (values: CitizenshipIntlItemModel['Purpose']) => {
    this.update({
      Purpose: values,
    });
  };

  updateDays = (values: CitizenshipIntlItemModel['Days']) => {
    this.update({
      Days: values,
    });
  };

  updateThreatened = (values: CitizenshipIntlItemModel['Threatened']) => {
    this.update({
      Threatened: values,
    });
  };

  updateThreatenedExplain = (values: CitizenshipIntlItemModel['ThreatenedExplain']) => {
    this.update({
      ThreatenedExplain: values,
    });
  };

  updateInfoGrab = (values: CitizenshipIntlItemModel['InfoGrab']) => {
    this.update({
      InfoGrab: values,
    });
  };

  updateInfoGrabExplain = (values: CitizenshipIntlItemModel['InfoGrabExplain']) => {
    this.update({
      InfoGrabExplain: values,
    });
  };

  updateInterest = (values: CitizenshipIntlItemModel['Interest']) => {
    this.update({
      Interest: values,
    });
  };

  updateInterestExplain = (values: CitizenshipIntlItemModel['InterestExplain']) => {
    this.update({
      InterestExplain: values,
    });
  };

  updateSecurityIncidents = (values: CitizenshipIntlItemModel['SecurityIncidents']) => {
    this.update({
      SecurityIncidents: values,
    });
  };

  updateSecurityIncidentsExplain = (values: CitizenshipIntlItemModel['SecurityIncidentsExplain']) => {
    this.update({
      SecurityIncidentsExplain: values,
    });
  };

  updateForeignContact = (values: CitizenshipIntlItemModel['ForeignContact']) => {
    this.update({
      ForeignContact: values,
    });
  };

  updateForeignContactExplain = (values: CitizenshipIntlItemModel['ForeignContactExplain']) => {
    this.update({
      ForeignContactExplain: values,
    });
  };

  updatePoliceEncountersExplain = (values: CitizenshipIntlItemModel['PoliceEncountersExplain']) => {
    this.update({
      PoliceEncountersExplain: values,
    });
  };

  updatePoliceEncounters = (values: CitizenshipIntlItemModel['PoliceEncounters']) => {
    this.update({
      PoliceEncounters: values,
    });
  };

  updateSearchedExplain = (values: CitizenshipIntlItemModel['SearchedExplain']) => {
    this.update({
      SearchedExplain: values,
    });
  };

  updateSearched = (values: CitizenshipIntlItemModel['Searched']) => {
    this.update({
      Searched: values,
    });
  };

  updateWhyTravel = (values: CitizenshipIntlItemModel['WhyTravel']) => {
    this.update({
      WhyTravel: values,
    });
  };

  updateWhenTraveled = (values: CitizenshipIntlItemModel['WhenTraveled']) => {
    this.update({
      WhenTraveled: values,
    });
  };

  updateCountryTraveledTo = (values: CitizenshipIntlItemModel['CountryTraveledTo']) => {
    this.update({
      CountryTraveledTo: values,
    });
  };

  render() {
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const isOther = Array.isArray(this.props.Purpose?.values) && this.props.Purpose.values.includes('Other');

    return (
      <>
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.countryTraveledTo')}
          attribute={`${attribute}.CountryTraveledTo`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocation
            {...this.props.CountryTraveledTo}
            attribute={`${attribute}.CountryTraveledTo`}
            layout="Country"
            onUpdate={this.updateCountryTraveledTo}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.whenTraveled')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          className="when-pvq no-margin-bottom"
        >
          <DateRange
            name="WhenTraveled"
            attribute={`${attribute}.WhenTraveled`}
            {...this.props.WhenTraveled}
            className="citizenship-when-date-pvq"
            minDateEqualTo
            onUpdate={this.updateWhenTraveled}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.lengthOfTrip')}
          attribute={`${attribute}.Days`}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <TravelDaysPVQ
            attribute={`${attribute}.Days`}
            name="Days"
            className="citizen-length-trip-pvq"
            {...this.props.Days}
            onUpdate={this.updateDays}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          attribute={`${attribute}.Purpose`}
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.whyTravel')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          <TravelPurpose
            attribute={`${attribute}.Purpose`}
            name="Purpose"
            className="citizenship-why-trip-pvq"
            {...this.props.Purpose}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            required={this.props.required}
            requireSingleSelection
          />
        </Field>
        {isOther && (
        <Field
          title={i18n.t('citizenship.pvq.additional.heading.citizenship.other')}
          attribute={`${attribute}.WhyTravel`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="WhyTravel"
            attribute={`${attribute}.WhyTravel`}
            {...this.props.WhyTravel}
            className="citizenship-why-trip-pvq"
            onUpdate={this.updateWhyTravel}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}
        <Branch
          name="Searched"
          {...this.props.Searched}
          attribute={`${attribute}.Searched`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.searched')}
          className="citizenship-searched"
          onUpdate={this.updateSearched}
          onError={this.props.onError}
          required={this.props.required}
        />

        {this.props.Searched?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.SearchedExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="SearchedExplain"
            attribute={`${attribute}.SearchedExplain`}
            {...this.props.SearchedExplain}
            className="citizenship-searched-explain-pvq"
            onUpdate={this.updateSearchedExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="PoliceEncounters"
          {...this.props.PoliceEncounters}
          attribute={`${attribute}.PoliceEncounters`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.policeEncounters')}
          className="citizenship-police-encounters"
          onUpdate={this.updatePoliceEncounters}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.PoliceEncounters?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.PoliceEncountersExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="PoliceEncountersExplain"
            attribute={`${attribute}.PoliceEncountersExplain`}
            {...this.props.PoliceEncountersExplain}
            className="citizenship-police-encounters-explain"
            onUpdate={this.updatePoliceEncountersExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="ForeignContact"
          {...this.props.ForeignContact}
          attribute={`${attribute}.ForeignContact`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.foreignContact')}
          className="citizenship-foreign-contact"
          onUpdate={this.updateForeignContact}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.ForeignContact?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.ForeignContactExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="ForeignContactExplain"
            attribute={`${attribute}.ForeignContactExplain`}
            {...this.props.ForeignContactExplain}
            className="citizenship-foreign-contact-explain"
            onUpdate={this.updateForeignContactExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="SecurityIncidents"
          {...this.props.SecurityIncidents}
          attribute={`${attribute}.SecurityIncidents`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.securityIncidents')}
          className="citizenship-sec-incidents"
          onUpdate={this.updateSecurityIncidents}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.SecurityIncidents?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.SecurityIncidentsExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="SecurityIncidentsExplain"
            attribute={`${attribute}.SecurityIncidentsExplain`}
            {...this.props.SecurityIncidentsExplain}
            className="citizenship-sec-incidents-explain"
            onUpdate={this.updateSecurityIncidentsExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="Interest"
          {...this.props.Interest}
          attribute={`${attribute}.Interest`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.interest')}
          className="citizenship-interest"
          onUpdate={this.updateInterest}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.Interest?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.InterestExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="InterestExplain"
            attribute={`${attribute}.InterestExplain`}
            {...this.props.InterestExplain}
            className="citizenship-interest-explain"
            onUpdate={this.updateInterestExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="InfoGrab"
          {...this.props.InfoGrab}
          attribute={`${attribute}.InfoGrab`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.infoGrab')}
          className="citizenship-info-grab"
          onUpdate={this.updateInfoGrab}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.InfoGrab?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.InfoGrabExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="InfoGrabExplain"
            attribute={`${attribute}.InfoGrabExplain`}
            {...this.props.InfoGrabExplain}
            className="citizenship-info-grab-explain"
            onUpdate={this.updateInfoGrabExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}

        <Branch
          name="Threatened"
          {...this.props.Threatened}
          attribute={`${attribute}.Threatened`}
          label={i18n.t('citizenship.pvq.additional.heading.citizenship.threatened')}
          className="citizenship-threatened"
          onUpdate={this.updateThreatened}
          onError={this.props.onError}
          required={this.props.required}
        />
        {this.props.Threatened?.value === 'Yes' && (
        <Field
          title={i18n.t('citizenship.pvq.additional.label.explain')}
          attribute={`${attribute}.ThreatenedExplain`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="ThreatenedExplain"
            attribute={`${attribute}.ThreatenedExplain`}
            {...this.props.ThreatenedExplain}
            className="citizenship-threatened-explain"
            onUpdate={this.updateThreatenedExplain}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}
      </>
    );
  }
}
